"use client";

import { type ReactNode, useState } from "react";
import { createGA4Tracking } from "./ga4";
import { GA4Tracker } from "./ga4-tracker";
import { TrackingContext, type TrackingContextValue } from "./use-tracking";

/**
 * ユーザートラッキング機能の公開エントリポイント（プラグイン形式）。
 *
 * GA4計測に必要な context 公開と副作用（script/auth/consent/user_id/pageview）を
 * 1コンポーネントに束ねているので、layout側は本Providerを配置するだけでよい。
 * tracker本体のマウントを忘れて計測が静かに壊れる、という運用リスクを構造的に排除する。
 *
 * 内部構造（非公開）:
 *  - TrackingContextProvider: tracking インスタンス生成と Context 公開
 *  - GA4Tracker: GA4への副作用すべて（script / auth / consent / user_id / pageview）
 *
 * コンポーネントツリーの依存方向:
 *   CookieConsentProvider（独立）→ UserTrackingProvider（tracking公開 + 副作用）
 *
 * - measurementId は呼び出し側(layout)から渡す。members/employersで別サブドメイン=別GA4プロパティのため、
 *   どちらのIDを使うかの判断はエントリポイントであるlayoutで明示する。
 * - excludedPathPattern（正規表現の source）に一致するパスの画面では GA4 へ送らない。
 *   どの画面を計測しないかは業務の都合なので、呼び出し側(layout)で決める。計測しない画面がなければ null を渡す。
 * - SSR中 / measurement ID未設定時は Context を null にし、末端の useTracking() は no-op、
 *   GA4Tracker も何も描画しない。
 */
export function UserTrackingProvider({
  measurementId,
  excludedPathPattern,
  children,
}: {
  measurementId: string | undefined;
  excludedPathPattern: string | null;
  children: ReactNode;
}) {
  return (
    <TrackingContextProvider
      measurementId={measurementId}
      excludedPathPattern={excludedPathPattern}
    >
      <GA4Tracker />
      {children}
    </TrackingContextProvider>
  );
}

/**
 * tracking インスタンスを生成して Context に公開する内部Provider。
 *
 * createGA4Tracking は内部で dataLayer/gtagスタブ・Consent Modeデフォルトを
 * セットアップする。Consent Modeの仕様上、gtag.jsロード *前* に dataLayer へ
 * 積む必要があるため、useState initializer（render中 = 子の GA4Tracker が
 * マウントする Script より先）で呼ぶ。
 */
function TrackingContextProvider({
  measurementId,
  excludedPathPattern,
  children,
}: {
  measurementId: string | undefined;
  excludedPathPattern: string | null;
  children: ReactNode;
}) {
  const [value] = useState<TrackingContextValue>(() => {
    if (typeof window === "undefined") {
      return null;
    }
    if (!measurementId) {
      return null;
    }
    return {
      tracking: createGA4Tracking(),
      measurementId,
      excludedPathPattern,
    };
  });

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
}
